<template>
  <!-- Lo propio de RITE en el diseño nuevo (#467): la matriz de las seis inspecciones y los datos de
       la instalación que cambian según la inspección. Los campos son los que dejó la #466 en la
       definición de la herramienta (`sup`, `zon`, `uta`, `emi`, `b1`…`b6`, `kw`, `ngc`): aquí sólo se
       colocan como en la maqueta, con las etiquetas y pistas de esa definición. -->
  <div class="ri">
    <CalcPanel titulo="Tipo de inspección" :pista="pistaTipo">
      <div class="ri-matrix" role="group" aria-label="Tipo de inspección">
        <span></span><span class="ri-colh">Periódica</span><span class="ri-colh">Inicial</span>
        <template v-for="f in familias">
          <span class="ri-lab" :key="'l' + f.k">{{ f.l }}</span>
          <template v-for="m in modos">
            <button v-if="celda(f.k, m)" :key="f.k + m" type="button" class="ri-cell" :data-fam="f.k"
              :aria-pressed="String(celda(f.k, m).clave === srv)" @click="$emit('srv', celda(f.k, m).clave)">
              <b>{{ celda(f.k, m).nombre }}</b>
              <small>{{ f.sub }}</small>
              <span class="ri-src" :class="f.src">{{ f.fuente }}</span>
            </button>
            <span v-else :key="f.k + m"></span>
          </template>
        </template>
      </div>
    </CalcPanel>

    <CalcPanel titulo="La instalación" pista="Común a frío y calor">
      <div class="cdn-grid">
        <div class="cdn-f"><label for="ri-sup">{{ etiqueta('sup', 'Superficie servida') }}</label><div class="cdn-unit"><input id="ri-sup" type="number" min="0" :step="paso('sup', 50)" :value="form.sup" @input="num('sup', $event)"><span>m²</span></div><span v-if="pista('sup')" class="cdn-ht">{{ pista('sup') }}</span></div>
        <div class="cdn-f"><label for="ri-zon">{{ etiqueta('zon', 'Edificios o zonas separadas') }}</label><input id="ri-zon" type="number" min="1" step="1" :value="form.zon" @input="num('zon', $event)"><span v-if="pista('zon')" class="cdn-ht">{{ pista('zon') }}</span></div>
        <template v-if="conFrio">
          <div class="cdn-f"><label for="ri-uta">{{ etiqueta('uta', 'UTAs / climatizadores') }}</label><input id="ri-uta" type="number" min="0" step="1" :value="form.uta" @input="num('uta', $event)"><span v-if="pista('uta')" class="cdn-ht">{{ pista('uta') }}</span></div>
          <div class="cdn-f"><label for="ri-emi">{{ etiqueta('emi', 'Splits, fan-coils, cassettes') }}</label><input id="ri-emi" type="number" min="0" step="1" :value="form.emi" @input="num('emi', $event)"></div>
        </template>
      </div>
      <p v-if="conFrio" class="cdn-note">Splits, fan-coils y cassettes se apuntan como información: no suman tiempo, porque no se miden uno a uno.</p>
    </CalcPanel>

    <CalcPanel v-if="conFrio" titulo="Generadores de frío" pista="Cantidad por potencia de cada equipo, nunca la total">
      <div class="ri-tw">
        <table class="ri-bands">
          <thead><tr><th>Potencia por equipo</th><th>Cantidad</th><th>1.er gen.</th><th>Adicional</th><th>Factor 2ª</th><th>Minutos</th></tr></thead>
          <tbody>
            <tr v-for="b in bandas" :key="b.k" :class="{ on: (form[b.k] || 0) > 0 }">
              <td>{{ b.l }}</td>
              <td><input type="number" min="0" step="1" :value="form[b.k]" :aria-label="'Cantidad ' + b.l" @input="num(b.k, $event)"></td>
              <td class="min">{{ b.c }}</td>
              <td class="min">{{ b.d }}</td>
              <td class="min">{{ coma(b.e.toFixed(2)) }}</td>
              <td class="min">{{ (form[b.k] || 0) > 0 ? Math.round(form[b.k] * b.d) : '—' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p class="cdn-note">Se cuenta un único «primer generador» para toda la instalación: todos van como adicionales y se suma una sola vez la mayor diferencia entre primero y adicional de los rangos con equipos.</p>
    </CalcPanel>

    <CalcPanel v-if="conCalor" titulo="Generadores de calor" :pista="pistaCalor">
      <div class="cdn-grid">
        <div class="cdn-f"><label for="ri-kw">{{ etiqueta('kw', 'Potencia térmica nominal') }}</label><div class="cdn-unit"><input id="ri-kw" type="number" min="0" :step="paso('kw', 5)" :value="form.kw" @input="num('kw', $event)"><span>kW</span></div></div>
        <div class="cdn-f"><label for="ri-ngc">{{ etiqueta('ngc', 'Nº de generadores de calor') }}</label><input id="ri-ngc" type="number" min="1" step="1" :value="form.ngc" @input="num('ngc', $event)"></div>
      </div>
      <!-- El aviso de < 70 / ≥ 70 kW es el de la definición de la herramienta (sus `alertas`), no un
           texto escrito aquí: si Dirección lo cambia en el editor, cambia en las dos pantallas. -->
      <div v-if="alertas.length" style="margin-top:10px">
        <div v-for="(a, i) in alertas" :key="'al' + i" class="cdn-state" :class="(form.kw || 0) >= 70 ? 'miss' : 'ok'">
          <span class="ico">{{ (form.kw || 0) >= 70 ? '!' : 'i' }}</span><div><b>{{ a }}</b></div>
        </div>
      </div>
    </CalcPanel>

    <!-- Si la definición trae algún campo más de los que este diseño coloca, no se pierde: sale aquí
         con su etiqueta. Hoy RITE no tiene ninguno; está para que un campo añadido desde el editor de
         herramientas no desaparezca de la pantalla sin avisar. -->
    <CalcPanel v-if="otros.length" titulo="Otros datos">
      <div class="cdn-grid">
        <template v-for="w in otros">
          <div v-if="w.tipo === 'number'" :key="w.campo" class="cdn-f"><label :for="'ri-o-' + w.campo">{{ w.label || w.campo }}</label><input :id="'ri-o-' + w.campo" type="number" :min="w.min != null ? w.min : 0" :step="w.step || 1" :value="form[w.campo]" @input="num(w.campo, $event)"><span v-if="w.hint" class="cdn-ht">{{ w.hint }}</span></div>
          <label v-else-if="w.tipo === 'checkbox'" :key="w.campo" class="cdn-chk" :class="{ on: !!form[w.campo] }"><input type="checkbox" :checked="!!form[w.campo]" @change="$emit('set', w.campo, $event.target.checked)">{{ w.label || w.campo }}</label>
          <div v-else-if="w.tipo === 'select'" :key="w.campo" class="cdn-f"><label :for="'ri-o-' + w.campo">{{ w.label || w.campo }}</label><select :id="'ri-o-' + w.campo" :value="form[w.campo]" @change="$emit('set', w.campo, $event.target.value)"><option v-for="o in w.options" :key="o.key" :value="o.key">{{ o.label || o.nombre }}</option></select></div>
        </template>
      </div>
    </CalcPanel>
  </div>
</template>

<script>
import CalcPanel from './../../../components/tarifas/diseno/CalcPanel.vue';

export default {
  components: { CalcPanel },
  props: {
    herramienta: { type: Object, required: true },
    form: { type: Object, required: true },
    srv: { type: String, default: '' },
    /** Familia de la inspección elegida: `calor` | `frio` | `mixta`. La decide la pantalla, igual que para el cálculo. */
    fam: { type: String, default: 'calor' },
    /** Las seis inspecciones: `[{ clave, nombre, fam, mod }]`, sacadas de los subtipos de la herramienta. */
    matriz: { type: Array, default: function () { return []; } },
    /** Los parámetros del motor (`RiteMotor.parametros`): las bandas de frío y la fórmula de calor. */
    parametros: { type: Object, required: true },
    alertas: { type: Array, default: function () { return []; } },
    /** Campos de la definición que este diseño no coloca por su nombre, ya filtrados por visibilidad. */
    otros: { type: Array, default: function () { return []; } }
  },
  data: function () {
    return {
      familias: [
        { k: 'calor', l: 'Calor', sub: 'Generadores de calor', src: 'act', fuente: 'Fórmula actual' },
        { k: 'frio', l: 'Frío', sub: 'Por rangos de potencia', src: 'xls', fuente: 'Excel' },
        { k: 'mixta', l: 'Mixta', sub: 'Frío + calor', src: 'mix', fuente: 'Excel + actual' }
      ],
      modos: ['per', 'ini']
    };
  },
  computed: {
    conFrio: function () { return this.fam !== 'calor'; },
    conCalor: function () { return this.fam !== 'frio'; },
    bandas: function () { return this.parametros.BANDS || []; },
    pistaTipo: function () {
      return this.fam === 'frio' ? 'Cálculo del Excel de frío'
        : this.fam === 'calor' ? 'Cálculo actual de calor' : 'Frío del Excel + calor actual';
    },
    pistaCalor: function () {
      const c = this.parametros.CALOR;
      const q = c && c.p ? (c.p[this.modoActual] || c.p.per) : null;
      if (!q) return '';
      return 'Fórmula actual: ' + q.base + ' + kW × ' + this.coma(q.kw) + ' + ' + this.coma(c.porGen) + ' por generador';
    },
    modoActual: function () {
      const self = this;
      const m = this.matriz.find(function (x) { return x.clave === self.srv; });
      return m ? m.mod : 'per';
    },
    /** Los widgets de la definición, por campo, para leer de ahí la etiqueta, la pista y el paso. */
    widgets: function () {
      const mapa = {};
      ((this.herramienta && this.herramienta.secciones) || []).forEach(function (sec) {
        (sec.widgets || []).forEach(function (w) { if (w && w.campo) mapa[w.campo] = w; });
      });
      return mapa;
    }
  },
  methods: {
    celda: function (fam, mod) {
      return this.matriz.find(function (x) { return x.fam === fam && x.mod === mod; }) || null;
    },
    etiqueta: function (campo, defecto) {
      const w = this.widgets[campo];
      if (!w || !w.label) return defecto;
      // La etiqueta de la definición lleva la unidad entre paréntesis («Superficie servida (m²)»);
      // aquí la unidad va dentro de la casilla, como en la maqueta, y no se repite. Sin expresión
      // regular: la que había retrocedía en cuadrático sobre una etiqueta con muchos espacios
      // (Sonar javascript:S5852), y la etiqueta la escribe cualquiera desde el editor de herramientas.
      // `trim` y no `trimEnd`, que no tienen los navegadores más viejos de `browserslist`.
      const label = String(w.label).trim();
      const unidad = ['(m²)', '(kW)'].find(function (u) { return label.endsWith(u); });
      return unidad ? label.slice(0, -unidad.length).trim() : label;
    },
    pista: function (campo) {
      const w = this.widgets[campo];
      return w && w.hint ? w.hint : '';
    },
    paso: function (campo, defecto) {
      const w = this.widgets[campo];
      return w && w.step ? w.step : defecto;
    },
    num: function (campo, ev) {
      this.$emit('set', campo, parseFloat(ev.target.value) || 0);
    },
    coma: function (v) { return String(v).replace('.', ','); }
  }
};
</script>

<style scoped>
.ri{display:flex;flex-direction:column;gap:14px;min-width:0}
.ri-matrix{display:grid;grid-template-columns:auto repeat(2,minmax(0,1fr));gap:8px;align-items:stretch}
.ri-lab{font-size:11px;text-transform:uppercase;letter-spacing:.08em;color:var(--muted);display:flex;align-items:center;font-weight:700}
.ri-colh{font-size:11px;text-transform:uppercase;letter-spacing:.08em;color:var(--muted);text-align:center;font-weight:700}
.ri-cell{border:1.5px solid var(--line);background:var(--surface);color:var(--fg);border-radius:9px;padding:10px 12px;text-align:left;cursor:pointer;display:flex;flex-direction:column;gap:3px;min-width:0}
.ri-cell b{font-size:13px}
.ri-cell small{font-size:11px;color:var(--muted)}
.ri-cell[data-fam="calor"]:hover{border-color:var(--heat)}
.ri-cell[data-fam="frio"]:hover{border-color:var(--cold)}
.ri-cell[data-fam="mixta"]:hover{border-color:var(--accent)}
.ri-cell[aria-pressed="true"][data-fam="calor"]{border-color:var(--heat);background:var(--heat-soft)}
.ri-cell[aria-pressed="true"][data-fam="frio"]{border-color:var(--cold);background:var(--cold-soft)}
.ri-cell[aria-pressed="true"][data-fam="mixta"]{border-color:var(--accent);background:var(--accent-soft)}
.ri-src{display:inline-flex;align-items:center;gap:5px;font-size:10.5px;font-weight:700;letter-spacing:.04em;text-transform:uppercase;padding:2px 7px;border-radius:999px;width:fit-content}
.ri-src.xls{background:var(--cold-soft);color:var(--cold)}
.ri-src.act{background:var(--heat-soft);color:var(--heat)}
.ri-src.mix{background:var(--accent-soft);color:var(--accent)}
.ri-tw{overflow-x:auto}
.ri-bands{width:100%;border-collapse:collapse;font-size:13px;color:var(--fg)}
.ri-bands th{font-size:10.5px;text-transform:uppercase;letter-spacing:.06em;color:var(--muted);text-align:right;font-weight:700;padding:4px 6px;border-bottom:1px solid var(--line)}
.ri-bands th:first-child{text-align:left}
.ri-bands td{padding:4px 6px;border-bottom:1px solid var(--line);text-align:right}
.ri-bands td:first-child{text-align:left;font-weight:600;white-space:nowrap}
.ri-bands input{width:64px;text-align:right;border:1px solid var(--line);background:var(--surface-2);color:var(--fg);border-radius:6px;padding:5px 7px}
.ri-bands tr.on td{background:var(--cold-soft)}
.ri-bands .min{font-family:var(--f-mono);font-variant-numeric:tabular-nums;color:var(--muted)}
.ri-bands tr.on .min{color:var(--fg)}
@media (max-width:480px){.ri-matrix{grid-template-columns:minmax(0,1fr) minmax(0,1fr)}.ri-matrix > span:first-child{display:none}.ri-lab{grid-column:1/-1}}
</style>
